import { Divider } from '@mui/material';

import { PropertyList } from '../../../components/Redesing/property-list';
import { PropertyListItem } from '../../../components/Redesing/property-list-item';
import PropertyListItemLink from '../../../components/Redesing/property-list-item-link';
import { SeverityPill } from '../../../components/Redesing/severity-pill';
import NoFilePlaceHolder from '../../../components/Redesing/no-file-place-holder';

import { IStation } from '../../../ts/interface/station.interface';

import {
  getCapacityUnitsName,
  showLocationCapacity,
  stationConfigurations,
} from '../../../util/stations';

import {
  convertCentsToMoney,
  openGoogleMapsCoordinates,
  stateFormatter,
  toTitleCase,
  zoneTypeFormatter,
} from '../../../utils';

import { LocationAccess } from '../../../ts/enums';

import useGetStationImages from '../../../hooks/stations/query/useGetStationImages';
import { useSystemRelay } from '../../../hooks/system/query/V2/useSystemRelay';
import ChangeOrganizationToSystem from '../../../hooks/system/hook/ChangeOrganizationToSystem';
import { ISystem } from '../../../ts/interface/system.interface';

function LocationDetailsCard({ detailedStation }: { detailedStation: IStation }) {
  const { data: stationImages } = useGetStationImages({
    enabled: false,
  });

  const {
    capacity,
    banner_id,
    lat,
    lng,
    state,
    module,
    reservations,
    maxReservations,
    configuration,
    zone_type,
    parking_credit,
    slow_zone_speed,
    radius,
    prevent_ending_rentals,
    prevent_starting_rentals,
    available_hardware,
    private: isPrivate,
    system_info: system,
  } = detailedStation;

  const { WAREHOUSE, RACK, STORE, ZONE } = stationConfigurations;
  const locationsHideBannerImg = [WAREHOUSE, STORE, ZONE];
  const hideBannerImg = locationsHideBannerImg.includes(configuration);
  const banner =
    stationImages?.filter((image: { id: string }) => image.id === banner_id) || [];

  // GraphQL-sourced modeInfo (getSystem by node id) so the organization view
  // does not depend on the system-scoped /currentsystem REST endpoint.
  const { modeInfo } = useSystemRelay(system?.id ?? undefined, {
    enabled: Boolean(system?.id),
  });

  const unitsName = getCapacityUnitsName(modeInfo?.currentMode);

  // @ts-ignore
  const { name: statusName, color: statusColor } = stateFormatter(state);

  return (
    <PropertyList>
      {configuration && (
        <PropertyListItem
          divider
          label="Location Type"
          value={
            configuration
              ? configuration === RACK
                ? 'Station'
                : toTitleCase(configuration)
              : ''
          }
        />
      )}
      <PropertyListItem
        divider
        label="Access"
        value={isPrivate ? LocationAccess.Private : LocationAccess.Public}
      />
      <>
        <ChangeOrganizationToSystem system={system || ({} as ISystem)} />
        <Divider />
      </>
      {configuration === ZONE ? (
        <>
          <Divider />
          <PropertyListItem label="Zone Type" value={zoneTypeFormatter(zone_type)} />
        </>
      ) : null}
      {configuration === ZONE ? null : (
        <PropertyListItemLink
          label="Lat/Long"
          text={`${lat}, ${lng}`}
          onClick={() => openGoogleMapsCoordinates(lat, lng)}
        />
      )}
      {configuration === ZONE ? (
        <>
          <Divider />
          <PropertyListItem
            label="Speed Limit"
            value={
              Number.isNaN(slow_zone_speed) || !slow_zone_speed
                ? 'None'
                : `${slow_zone_speed} km/h`
            }
          />
        </>
      ) : null}
      {![ZONE].includes(configuration) ? (
        <>
          <Divider />
          <PropertyListItem
            label="Radius"
            value={!radius ? 'None' : `${radius} ${radius === 1 ? 'Meter' : 'Meters'}`}
          />
        </>
      ) : null}
      <Divider />
      <PropertyListItem label="Status" value={statusName} dataId="status-label">
        <SeverityPill color={statusColor}>{statusName}</SeverityPill>
      </PropertyListItem>
      {showLocationCapacity(configuration, modeInfo?.currentMode) ? (
        <>
          <Divider />
          <PropertyListItem label={`Number of ${unitsName}`}>
            <SeverityPill
              color={
                capacity && capacity > 0 ? 'success' : !capacity ? 'success' : 'warning'
              }
            >
              {capacity || 'Unlimited'}
            </SeverityPill>
          </PropertyListItem>
        </>
      ) : null}
      <Divider />
      <PropertyListItem label="Availability" value={available_hardware || 0}>
        <SeverityPill
          color={available_hardware && available_hardware > 0 ? 'success' : 'warning'}
        >
          {available_hardware || 0}
        </SeverityPill>
      </PropertyListItem>
      {modeInfo?.currentMode === 'bikeRoom' ? (
        <>
          <Divider />
          <PropertyListItemLink
            to={`/units/locks/${module}`}
            text={module || 'None'}
            label={'Lock'}
          />
        </>
      ) : null}
      {system?.reservations ? (
        <div>
          <Divider />
          <PropertyListItem
            label="Allow Reservations"
            value={reservations !== null && reservations ? 'Yes' : 'No'}
          />
          {reservations || (reservations === null && Boolean(system?.reservations)) ? (
            <>
              <Divider />
              <PropertyListItem
                label="Max Reservations"
                value={
                  maxReservations ||
                  (system?.maxReservations ? system?.maxReservations : 'None')
                }
              />
            </>
          ) : null}
        </div>
      ) : null}
      {parking_credit ? (
        <>
          <Divider />
          <PropertyListItem
            label="Parking Credit"
            value={convertCentsToMoney(parking_credit)}
          />
        </>
      ) : null}
      {!hideBannerImg && (
        <>
          <Divider />
          <PropertyListItem label="Banner Image">
            {banner.length > 0 ? (
              <div className="imageContainer">
                {banner.length > 0 ? (
                  !banner[0].approved_on && !banner[0].rejected ? (
                    <i className="fa fa-hourglass-half fa-fw navIconStyle navIcon pull-right" />
                  ) : banner[0].rejected ? (
                    <i className="fa fa-ban fa-fw navIconStyle navIcon pull-right" />
                  ) : banner[0].approved_on ? (
                    <i className="fa fa-check-circle fa-fw navIconStyle navIcon pull-right">
                      {' '}
                    </i>
                  ) : null
                ) : null}
                <img
                  alt=""
                  className="banner_image"
                  src={`data:image/png;base64,${banner[0].image}`}
                />
                <div className="stationOverlay">
                  <div className="stationText imageText">
                    {!banner[0].approved_on && !banner[0].rejected
                      ? 'Image is being reviewed by Movatic.'
                      : banner[0].rejected
                      ? 'Image has been rejected, due to poor quality or due to cropping issues.'
                      : banner[0].approved_on
                      ? 'Image has been approved.'
                      : null}
                  </div>
                </div>
              </div>
            ) : (
              <NoFilePlaceHolder />
            )}
          </PropertyListItem>
        </>
      )}
      <Divider />
      {[
        {
          label: 'Prevent ending rentals',
          key: 'preventEndingRentals',
          item: prevent_ending_rentals ? 'Yes' : 'No',
        },
        {
          label: 'Prevent starting rentals',
          key: 'preventStartingRentals',
          item: prevent_starting_rentals ? 'Yes' : 'No',
        },
      ].map(({ item, key, label }) => (
        <PropertyListItem divider key={key} label={label} value={item}>
          <SeverityPill color={item === 'Yes' ? 'warning' : 'success'}>
            {item}
          </SeverityPill>
        </PropertyListItem>
      ))}
    </PropertyList>
  );
}

export default LocationDetailsCard;
